"use client";

import { useRouterState } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { ActiveNotificationIcon, PaidIcon, SearchIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { shouldShowCommunityNavigation } from "@/components/should-show-community-navigation";
import { useSession } from "@/hooks/use-session";

import { Logo } from "./logo";
import { NotificationBell } from "./notifications/notification-bell";
import { SearchInput } from "./search/search-input";
import UserMenu from "./user-menu";

interface HeaderProps {
  showSearch?: boolean;
  sticky?: boolean;
}

export default function Header({ showSearch = true, sticky = true }: HeaderProps) {
  const t = useTranslations("globals.header");
  const ta = useTranslations("globals.accessibility");
  const tOffers = useTranslations("shared.affiliates");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: session } = useSession();
  const isSearchPage = pathname?.includes("/search");
  const showCommunityNavigation = shouldShowCommunityNavigation(session?.user);

  return (
    <header
      className={`${sticky ? "sticky top-0" : "relative"} z-50 w-full bg-card border-b border-border px-4 md:px-10 lg:px-20 py-3`}
    >
      <div className="flex items-center gap-4 md:gap-8 max-w-[1440px] mx-auto">
        <Link
          to="/"
          aria-label={t("homeLink")}
          className="flex min-h-11 items-center gap-3 text-primary shrink-0"
        >
          <Logo className="size-8" />
          <span className="text-xl font-bold leading-tight tracking-tight hidden sm:block">
            Garimpa.Club
          </span>
        </Link>

        {showSearch && !isSearchPage && (
          <>
            <Link
              to={"/search#focus"}
              aria-label={ta("search")}
              className="relative after:absolute after:-inset-0.5 after:content-[''] md:hidden flex items-center justify-center size-10 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <SearchIcon />
            </Link>
            <SearchInput className="hidden md:flex flex-1" />
          </>
        )}

        <div className="flex shrink-0 ml-auto gap-4 items-center">
          <nav className="hidden xl:flex items-center gap-8">
            <Link
              to="/alerts"
              className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary"
            >
              <ActiveNotificationIcon className="text-lg" decorative />
              {t("alerts")}
            </Link>
            <Link
              to={"/offers"}
              className="inline-flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors"
            >
              <PaidIcon className="text-lg" aria-hidden="true" />
              {tOffers("offersNavLabel")}
            </Link>
            {showCommunityNavigation && (
              <Link
                to={"/community"}
                className="text-sm font-medium hover:text-primary transition-colors"
              >
                {t("community")}
              </Link>
            )}
          </nav>
          <NotificationBell />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
